 <template>
    <div class="form-property-details">
        <ElForm class="form-property-details__form form"
            ref="propertyFormRef"
            :rules="rules"
            :model="propertyForm"
            >
            <ElFormItem class="form__item"
                prop="address_object">
                <ElAutocomplete v-model="propertyForm.address_object"
                    placeholder="Адрес нахождения имущества"
                    :fetch-suggestions="fetchAddressSuggestions"
                    :popper-append-to-body="false"
                    value-key="value"
                    v-on:input="clearParams"
                    @select="handleSelect"
                    >
                    <template #prefix>
                        Адрес нахождения имущества
                    </template>
                </ElAutocomplete>
            </ElFormItem>

            <ElFormItem
                v-show="propertyForm.isSetSuggest && propertyForm.isSetSuggestManual"
                class="form__item"
                prop="cadastral_number">
                <ElInput v-model="propertyForm.cadastral_number"
                         placeholder="Кадастровый номер">
                    <template #prefix>
                        Кадастровый номер
                    </template>
                </ElInput>
            </ElFormItem>

            <ElFormItem v-if="!!areaPurposes.length"
                class="form__item"
                prop="purpose_object">
                <ElSelect v-model="propertyForm.purpose_object"
                    placeholder="Основное назначение помещения"
                    :popper-append-to-body="false">
                    <template #prefix>
                        Основное назначение помещения
                    </template>
                    <ElOption v-for="option in areaPurposes"
                        :key="option.xml"
                        :label="option.name"
                        :value="option.name">
                    </ElOption>
                </ElSelect>
            </ElFormItem>

            <div class="form__item form__item--col">
                <ElFormItem class="form__col form__col--half"
                    prop="area_object">
                    <ElInput
                        v-model="propertyForm.area_object"
                        v-mask="{mask: '9{0,3}.9{0,3} м²'}"
                        placeholder="Площадь помещения, м²"
                        @input="inputArea"
                    >
                        <template #prefix>
                            Площадь помещения, м²
                        </template>
                    </ElInput>
                </ElFormItem>
                <ElFormItem class="form__col form__col--half"
                    prop="date_start_police">
                    <ElDatePicker v-model="propertyForm.date_start_police"
                        v-mask="{mask: '9{2}.9{2}.9{4}'}"
                        type="date"
                        placeholder="Выберите дату"
                        format="dd.MM.yyyy"
                        value-format="dd.MM.yyyy"
                        :picker-options="{ disabledDate: (time) => getDisabledStartDate(time) }"
                        >
                    </ElDatePicker>
                    <span class="form__label">Дата начала полиса</span>
                </ElFormItem>
            </div>

            <ElFormItem class="form__item">
                <ElInput v-model="propertyForm.coupon"
                placeholder="Введите промокод"
                @input="debouncedCouponHandler"
                >
                    <template #prefix>
                        Промокод
                    </template>
                </ElInput>
            </ElFormItem>

            <p v-if="!!propertyForm.date_start_police?.length"
                class="form-property-details__comment">
                Ваш полис будет действовать до {{ policyEndDate }}.
                <br>
                Для его продления {{ policyProlongationDate }} и далее ежемесячно,
                <br>
                мы автоматически спишем с Вашей привязанной карты {{ price }} рублей.
                <br>
                Отказаться от подписки Вы сможете в любой момент по <a class="form-property-details__terms-agree-text--link" @click="$emit('cancel-subscription')">ссылке</a>
            </p>

            <div class="form-property-details__terms-agree">
                <el-checkbox v-model="agreedWithTerms"></el-checkbox>
                <p class="form-property-details__terms-agree-text">
                    Оплатой страховой премии подтверждаю ознакомление
                    и согласие с данными указанными в полисе и
                    <a class="form-property-details__terms-agree-text--link"
                        href="https://zettains.ru/upload/iul/Правила_ИПО_на_сайт.pdf"
                        target="_blank">правилах страхования</a>,
                    достоверность сведений указанных при оформлении
                    <a class="form-property-details__terms-agree-text--link"
                        href="https://zettains.ru/upload/iul/Полис-оферта_образец.pdf"
                        target="_blank">договора страхования</a>.
                        Даю согласие на передачу и обработку
                        <a class="form-property-details__terms-agree-text--link"
                        href="https://zettains.ru/upload/accounting/%D0%9F%D0%BE%D0%BB%D0%B8%D1%82%D0%B8%D0%BA%D0%B0%20%D0%BE%D0%B1%D1%80%D0%B0%D0%B1%D0%BE%D1%82%D0%BA%D0%B8%20%D0%BF%D0%B5%D1%80%D1%81%D0%BE%D0%BD%D0%B0%D0%BB%D1%8C%D0%BD%D1%8B%D1%85%20%D0%B4%D0%B0%D0%BD%D0%BD%D1%8B%D1%85.pdf"
                        target="_blank">персональных данных.</a>
                </p>
            </div>

            <div class="form__buttons form__buttons--margin-small">
                <BaseButton text="Назад"
                    theme="muted"
                    is-text
                    @click.native="$emit('prev-step')"
                    />
                <BaseButton text="Оплатить"
                    size="large"
                    :is-disabled="submitIsDisabled"
                    @click.native="onSubmit($refs.propertyFormRef, 'default-payment')" />
            </div>

            <BaseButton class="form-property-details__button-send-link"
                text="Отправить ссылку на оплату"
                is-text
                size="small"
                underline-text
                @click.native="onSubmit($refs.propertyFormRef, 'link-payment')" />
        </ElForm>
    </div>
</template>

<script>
import { parse, format, addMonths, subDays, addDays } from 'date-fns';
import BaseButton from '@/components/ui/BaseButton';
import { CADASTRAL_CODE_LENGTH } from '@/consts';
import { numbersLengthFromString, fieldsAreFilled, getNumbersFromString } from '@/utils/string-helpers';
import { validationHandler } from '@/utils/validation';
import { fetchAddressSuggestions, debounce } from '@/utils/api-helpers';
import { validateCoupon } from '@/api/routes/coupon';
import { getAreaPurposes } from '@/api/routes/areas';

export default {
    name: 'FormClientDetails',
    components: {
        BaseButton
    },
    props: {
        price: {
            type: String,
            required: true
        }
    },
    data() {
        return {
            propertyForm: {
                address_object: '',
                cadastral_number: '',
                purpose_object: '',
                area_object: '',
                date_start_police: '',
                coupon: '',
                object_fias_id: '',
                object_house: '',
                object_houseGuid: '',
                isSetSuggest: false,
                isSetSuggestManual: false,
            },
            rules: {
                address_object: [
                    { required: true, message: 'Поле aдреса обязательно для заполнения', trigger: 'blur' },
                    {
                        validator: (rule, val, resolve) => validationHandler(this.addressIsValid, this.addressError, resolve),
                        trigger: 'blur'
                    }
                ],
                cadastral_number: [
                    {
                        validator: (rule, value, resolve) => validationHandler(this.cadastralValid, `Поле обязательно для заполнения`, resolve),
                        trigger: 'blur'
                    },
                    {
                        validator: (rule, value, resolve) => validationHandler(this.cadastralNumberLengthIsValid, `Кадастровый номер должен содержать не более ${CADASTRAL_CODE_LENGTH} символов`, resolve),
                        trigger: 'blur'
                    },
                ],
                purpose_object: [
                    { required: true, message: 'Назначение помещения обязательно для заполнения', trigger: 'submit' }
                ],
                area_object: [
                    { required: true, message: 'Площадь помещения обязательна для заполнения', trigger: 'blur' },
                ],
                date_start_police: [
                    {
                        validator: (rule, val, resolve) => {
                            validationHandler(this.validatePolicyStartDate, `Дата начала полиса меньше текущей даты`, resolve)
                        },
                        trigger: 'blur'
                    },
                    { required: true, message: 'Выберите дату начала полиса', trigger: 'change' },
                ],
            },
            agreedWithTerms: false,
            areaPurposes: []
        }
    },
    methods: {
        onSubmit(formRef, paymentType) {
            formRef.validate(valid => {
                if (valid) {
                    this.$emit('form-submit', this.propertyForm, paymentType)
                }
            })
        },
        getDisabledStartDate(date) {
            let currentDay = Date.now();
            let maxLimitDay = new Date();
            let numberOfDaysToAdd = 45;
            maxLimitDay.setDate(maxLimitDay.getDate() + numberOfDaysToAdd);
            return date.getTime() < currentDay || date.getTime() > maxLimitDay.getTime();
        },
        async handleCoupon() {
            const couponResp = await validateCoupon(this.propertyForm.coupon);
            const discountPercent = this.validateCouponResponse(couponResp);

            this.$emit('coupon-discount', discountPercent);
        },
        validatePolicyStartDate() {
            if (!this.propertyForm.date_start_police) {
                return false
            } else return true
        },
        validateCouponResponse(response) {
            return response?.result === true && !!response?.discount
                ? response.discount
                : 0;
        },
        inputArea(value) {
          let numberValue = getNumbersFromString(value, true);
          if (numberValue >= 999) {
            this.propertyForm.area_object = '999.0 м²';
          }
        },
        clearParams(val) {
            this.propertyForm.isSetSuggest = false;
            this.propertyForm.isSetSuggestManual = false;
            this.propertyForm.object_house = '';
            this.propertyForm.object_houseGuid = '';
            this.propertyForm.object_fias_id = '';
        },
        handleSelect(item) {
            this.propertyForm.isSetSuggest = true;

            if (item?.code && item.code === 'user_manual') {
                this.propertyForm.isSetSuggestManual = true;
            }

            this.propertyForm.object_house = item.house;
            this.propertyForm.object_houseGuid = item.houseGuid;
            this.propertyForm.object_fias_id = item.fiasId;

            this.$refs.propertyFormRef.validateField('address_object');
        },
    },
    computed: {
        addressError() {
            if (!this.propertyForm.isSetSuggest) {
                return 'Не выбран адрес из списка';
            }

            if (this.propertyForm.isSetSuggest && !this.propertyForm.isSetSuggestManual && !!!this.propertyForm.object_houseGuid) {
                return 'Не указан номер дома';
            }

            return 'Поле обязательно для заполнения'
        },
        addressIsValid() {
            if (!this.propertyForm.isSetSuggest) {
                return false;
            }

            if (this.propertyForm.isSetSuggest && !this.propertyForm.isSetSuggestManual && !!!this.propertyForm.object_houseGuid) {
                return false;
            }

            return true;
        },
        submitIsDisabled() {
            if (
                !!!this.propertyForm.address_object.length
                || !!!this.propertyForm.area_object.length
                || !!!this.propertyForm.date_start_police.length
            ) {
                return true;
            }

            if (!this.propertyForm.isSetSuggest) {
                return true;
            }

            if (this.propertyForm.isSetSuggest && this.propertyForm.isSetSuggestManual && !!!this.propertyForm.cadastral_number.length) {
                return true;
            }

            if (!this.agreedWithTerms) {
                return true;
            }

            return false;
        },
        cadastralNumberLengthIsValid() {
            if (!this.propertyForm.isSetSuggestManual) {
                return true;
            }

            return numbersLengthFromString(this.propertyForm.cadastral_number) <= CADASTRAL_CODE_LENGTH;
        },
        cadastralValid() {
            if (!this.propertyForm.isSetSuggestManual) {
                return true;
            }

            return this.propertyForm.isSetSuggest && this.propertyForm.isSetSuggestManual && !!this.propertyForm.cadastral_number.length;
        },
        debouncedCouponHandler() {
            const debounceMs = 300;
            return debounce(this.handleCoupon, debounceMs);
        },
        startPoliceDate() {
            const dateString = this.propertyForm.date_start_police;
            return dateString?.length
                ? parse(dateString, 'dd.MM.yyyy', new Date())
                : ''
        },
        policyEndDate() {
            if (!!this.startPoliceDate) {
                const datePlusMonth = addMonths(this.startPoliceDate, 1);
                const dateMinusDay = subDays(datePlusMonth, 1);
                return format(dateMinusDay, 'dd.MM.yyyy');
            } else return ''
        },
        policyProlongationDate() {
            if (!!this.startPoliceDate) {
                const datePlus25Days = addDays(this.startPoliceDate, 25)
                return format(datePlus25Days, 'dd.MM.yyyy');
            } else return ''
        },

        fetchAddressSuggestions
    },

    async mounted() {
        this.areaPurposes = await getAreaPurposes();
    }
}
</script>

<style scoped lang=scss>
.form-property-details {

    &__form {
        display: flex;
        flex-direction: column;
    }

    &__comment {
        @include text-normal($font-weight-regular, $line-height-normal);

        margin-top: rem($gap-big);

        @media #{$screen-medium} {
            font-size: rem($font-size-small);

            margin-top: rem($gap-medium);
        }

        @media #{$screen-small} {
            font-size: rem($font-size-mini);
        }
    }

    &__terms-agree {
        margin-top: rem($gap-normal);

        display: flex;

        &-text {
            @include text-normal($font-weight-regular, $line-height-normal);

            margin-left: rem($gap-mini);

            @media #{$screen-medium} {
                font-size: rem($font-size-small);
            }

            @media #{$screen-small} {
                font-size: rem($font-size-mini);
            }

            &--link {
                text-decoration: underline;

                color: inherit;
            }
        }
    }

    &__button-send-link {
        margin-top: rem($gap-mini);

        align-self: flex-end;
    }
}
</style>
